export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'playerso_theme';

/** Detent order used by the theme switch — also the toggle cycle order. */
export const THEMES: readonly Theme[] = ['light', 'dark'] as const;

function isTheme(value: unknown): value is Theme {
  return value === 'light' || value === 'dark';
}

/**
 * Dark is the product default. An explicit choice in localStorage always wins
 * so a user who switches to light isn't flipped back on the next visit.
 */
/**
 * Reads the stored theme. `winter` was a previously-selectable theme that has
 * been folded into `dark` (snow now follows dark mode automatically), so any
 * stored `winter` value is migrated to `dark` on read.
 */
function readStoredTheme(): Theme | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === 'winter') return 'dark';
    return isTheme(raw) ? raw : null;
  } catch {
    return null;
  }
}

/**
 * `dark` sets `.dark`; everything else clears it. The `.dark` class is always
 * removed on the light theme, which also fixes a stale `.dark` left behind by
 * older versions of this file.
 */
export function applyTheme(theme: Theme) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.classList.toggle('dark', theme === 'dark');
  root.style.colorScheme = theme === 'dark' ? 'dark' : 'light';
}

/**
 * Applied before React mounts to avoid a light flash on first paint.
 */
export function initTheme(): Theme {
  const stored = readStoredTheme();
  const theme: Theme = stored ?? 'dark';
  applyTheme(theme);
  return theme;
}

export function persistTheme(theme: Theme) {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    /* storage unavailable (private mode) — theme still applies for this page */
  }
}

/** Browser chrome colour per theme, so the mobile URL bar matches the page. */
export function themeColor(theme: Theme): string {
  if (theme === 'dark') return '#000000';
  return '#ffffff';
}